import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  PanelRightClose,
  PanelRightOpen,
  AlertCircle,
  Inbox,
  Clock,
  ExternalLink,
  HelpCircle,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useChat } from "../contexts/ChatContext";
import { conversationsApi, POLL_MS } from "../services/conversationsApi";
import { renderMarkdownSafe } from "../utils/markdown";
import { formatAbsolute, formatClock, formatRelative, formatDay, dayKey } from "../utils/datetime";
import { isHexColor, readableForeground } from "../utils/contrastColor";
import { useChatWidgetConfig } from "../hooks/useChatWidgetConfig";
import { useConversationLive } from "../hooks/useConversationLive";
import {
  readScopedCustomProperty,
  scopePanelCustomCss,
} from "../utils/panelCustomCss";
import { cardsTemplate } from "../utils/productCards";
import ProductCardStrip from "./ProductCardStrip";
import AttachmentBlock from "./AttachmentBlock";
import { STATUS_KEYS, ConversationStatusPill } from "./ConversationStatusDot";
import ConversationBotTag from "./ConversationBotTag";
import ContactAvatar, { AgentAvatar, OperatorAvatar } from "./ContactAvatar";
import { COLUMN_HEADER_CLASS } from "./conversationLayout";
import Skeleton from "./Skeleton";
import ConversationCsat from "./ConversationCsat";
import { pageMarkers } from "../utils/conversationPages";
import { clipText } from "../utils/contextAttributes";

// A classe do contentor da transcricao, que e ao mesmo tempo a raiz do @scope
// do CSS do tenant e o elemento onde vivem os tokens do widget (index.css).
// Esta constante e a classe no JSX tem de andar juntas.
const TRANSCRIPT_CLASS = "transcript";

const ROLE_KEYS = {
  user: "conversationsRoleUser",
  assistant: "conversationsRoleAssistant",
  system: "conversationsRoleSystem",
  tool: "conversationsRoleTool",
  operator: "conversationsRoleOperator",
};

// A linha que a ponte de handoff escreve quando a transferencia faz commit
// (spec da fase 5, seccao 1): `role = system`, `content` vazio e `kind:
// "handoff"`. Nao e nada que alguem tenha dito, e desenha-se como divisor.
const isHandoffDivider = (m) => m.role === "system" && m.kind === "handoff";

// Quem fala numa mensagem: o papel e, nas do operador, o id de quem a escreveu
// no Chatwoot. Uma conversa que passa de um operador para outro fica com uma
// assinatura por operador, e nao com as palavras dos dois debaixo do nome do
// primeiro. Uma automacao do Chatwoot chega sem `sender` e fica com a sua; um
// `sender` sem `id`, que o contrato da T7 admite, agrupa-se pelo nome. O
// divisor do handoff e um turno so dele.
const speakerKey = (m) => {
  if (isHandoffDivider(m)) return `handoff:${m.id}`;
  if (m.role === "operator") return `operator:${m.sender?.id ?? m.sender?.name ?? ""}`;
  return m.role;
};

// O backend parte uma resposta do agente em varias mensagens, portanto um turno
// unico chegava aqui como cinco ou seis caixas iguais, cada uma a repetir o
// nome de quem falou e a hora. Dobrar as mensagens consecutivas de quem fala
// devolve a transcricao a forma de conversa: um rotulo e uma hora por turno.
const groupIntoTurns = (messages) =>
  (messages || []).reduce((acc, m) => {
    const last = acc[acc.length - 1];
    if (last && last.speaker === speakerKey(m)) {
      last.items.push(m);
      return acc;
    }
    acc.push({ speaker: speakerKey(m), role: m.role, at: m.created_at, items: [m] });
    return acc;
  }, []);

const blocksOfType = (message, type) => (message.blocks || []).filter((b) => b.type === type);

// Quatro ferramentas do backend escrevem blocos `cards` e so uma delas manda
// cartoes de produto: o send_product_cards_tool. As outras tres, o
// send_curated_cards_tool, o send_choice_options_tool e o
// send_end_conversation_button_tool, mandam a mesma forma de arame com um
// `template` la dentro, e o widget separa-as por ele para as levar a quatro
// renderizadores diferentes.
const cardBlocksOfTurn = (turn) => turn.items.flatMap((m) => blocksOfType(m, "cards"));

// Os cartoes de produto de um turno chegam um por mensagem
// (send_product_cards_tool manda um item por bloco), portanto juntam-se todos
// numa tira horizontal em vez de empilharem uma caixa por produto.
const productCardItemsOfTurn = (turn) =>
  cardBlocksOfTurn(turn)
    .filter((b) => cardsTemplate(b.payload?.items) === "product")
    .flatMap((b) => b.payload?.items || []);

const hasTitles = (block) => (block.payload?.items || []).some((item) => item.title);

// Uma tira de opcoes ou um card curado: algo que o cliente viu para ler. Nao levam
// o cartao de produto, que lhes assentava mal: uma tira de opcoes vem com
// `uri: "#"`, que o parsePageUrl recusa, e sem preco nenhum, portanto desenhava-se
// como um cartao mudo com uma foto e um titulo. A oferta do botao de terminar
// conversa fica de fora: e uma anotacao (ver abaixo).
const otherCardBlocksOfTurn = (turn) =>
  cardBlocksOfTurn(turn).filter((b) => !["product", "end_conversation"].includes(cardsTemplate(b.payload?.items)));

// O que a oferta do botao de terminar conversa, cujo rotulo e do widget e nao do
// item, ou um bloco sem itens dizem ao operador e so a nota que o writer escreveu
// para esta superficie. Nao e nada que o agente tenha dito, portanto desenha-se
// como anotacao do turno, ao centro da transcricao, e nao na coluna do agente.
const isAnnotation = (block) =>
  cardsTemplate(block.payload?.items) === "end_conversation" || (block.payload?.items || []).length === 0;

const annotationNotesOfTurn = (turn) =>
  cardBlocksOfTurn(turn)
    .filter((b) => isAnnotation(b) && b.payload?.operator_note)
    .map((b) => b.payload.operator_note);

// A rede para tipos de bloco futuros: um tipo que ninguem desenhou aparece como
// o seu nome em vez de desaparecer sem deixar rasto. O `context` fica de fora
// porque nao e nada que alguem tenha dito, sao os atributos que o browser do
// cliente mandou com a mensagem, e o sitio deles e o painel lateral. Sem esta
// exclusao a palavra "context" nascia no meio das conversas no dia em que o
// backend passasse a escrever o bloco. O bloco continua fora do transcript e so
// a mudanca de pagina aparece, como marca (PageDivider).
const KNOWN_BLOCK_TYPES = ["cards", "attachment", "context", "csat_request"];

const unknownBlocks = (message) =>
  (message.blocks || []).filter((b) => !KNOWN_BLOCK_TYPES.includes(b.type));

// Uma mensagem do cliente a caminho do operador so se marca quando ha razao para
// alguem olhar (spec da fase 5, seccao 7). `rejected` e o fim: a ponte desistiu,
// ao fim de cinco tentativas ou logo, com um anexo que ja nao existe, e o
// operador nunca a vai ver. Numa conversa aberta, um `pending` so se marca
// passado um minuto, porque uma entrega normal leva menos de um segundo e um
// `pending` recente e so a ponte a trabalhar. O minuto conta-se do `created_at`
// contra o relogio deste browser. `forwarded`, ou sem estado nenhum, e o estado
// calmo e nao leva marca.
//
// Um `pending` numa conversa que ja fechou e incerto, e diz-se assim (decidido
// pelo Tiago a 2026-10-01). O fecho impede que um envio comece, mas um que ja
// estava a caminho acaba na mesma: pode gravar o `forwarded` ate ao
// LONGEST_SEND_S do backend (45 s) depois do fecho, ou acabar ambiguo e ficar
// `pending` para sempre, porque nada volta a despachar uma conversa fechada. O
// backoffice so recebe o estado, e nao o `ambiguous`, portanto nao separa "nunca
// saiu" de "pode ter chegado". No primeiro minuto depois do fecho vale a regra da
// conversa aberta; passado esse minuto, a marca diz que pode nao ter chegado.
const LATE_BRIDGE_MS = 60000;
const CLOSED_GRACE_MS = 60000;

const bridgeMark = (message, now, resolvedAt) => {
  if (message.bridge_status === "rejected") return "rejected";
  if (message.bridge_status !== "pending") return null;
  if (resolvedAt && now - Date.parse(resolvedAt) > CLOSED_GRACE_MS) return "uncertain";
  return now - Date.parse(message.created_at) > LATE_BRIDGE_MS ? "late" : null;
};

// Como cada marca se desenha. As duas que nao sao uma falha certa ficam na cor
// secundaria; so o `rejected`, o fim, leva o vermelho.
const BRIDGE_MARKS = {
  late: { Icon: Clock, color: "var(--color-text-secondary)", labelKey: "conversationsBridgeLate" },
  uncertain: { Icon: HelpCircle, color: "var(--color-text-secondary)", labelKey: "conversationsBridgeUncertain" },
  rejected: { Icon: AlertCircle, color: "var(--color-error-text)", labelKey: "conversationsBridgeRejected" },
};

const BridgeMarkLine = ({ mark, translate }) => {
  const { Icon, color, labelKey } = BRIDGE_MARKS[mark];
  return (
    <p className="flex items-center gap-1 text-xs" style={{ color }}>
      <Icon size={12} aria-hidden="true" />
      {translate(labelKey)}
    </p>
  );
};

// O operator_note e escrito para a superficie do agente humano, que e esta. Os
// writers embrulham-no em asteriscos de markdown ("*Cartao de produto enviado
// para o cliente:* {url}") porque no Chatwoot ele vira o `content` de uma
// mensagem. Aqui tiram-se os asteriscos em vez de aparecerem crus.
const stripEmphasis = (text) => text.replace(/\*/g, "");

// A geometria e a do widget (ai-agent-widget/app/src/ui/bubble.css): raio de
// 20px, canto de 4px do lado de quem fala, e mais um canto de 4px em cima
// quando a bolha continua o mesmo turno. Sistema e ferramenta nao sao balao de
// fala nenhum, portanto ficam num rectangulo simples.
const QUIET_RADIUS = { borderRadius: "8px" };

const bubbleRadius = (side, continuesRun) => {
  if (!side) return QUIET_RADIUS;
  const corner = side === "end" ? "Right" : "Left";
  return {
    borderRadius: "20px",
    [`borderBottom${corner}Radius`]: "4px",
    ...(continuesRun ? { [`borderTop${corner}Radius`]: "4px" } : {}),
  };
};

// A primaria do chat pinta so o balao do agente. O cliente, o operador e a
// maquinaria ficam nos tokens da casa, que ja respondem ao tema claro e escuro
// sozinhos.
//
// `align` poe a coluna do turno do lado certo; `body` alinha a prosa DENTRO do
// balao, que fica a esquerda mesmo com o balao encostado a direita. Texto a
// ferro na direita nao se le, e nem o widget nem o Chatwoot o fazem: o que muda
// de lado e o balao, nao as linhas.
const TURN_SHAPES = {
  assistant: {
    side: "end",
    align: "items-end",
    body: "text-left",
    bubbleClass: "transcript-bubble-agent",
    bubble: {
      backgroundColor: "var(--transcript-agent-bg)",
      color: "var(--transcript-agent-fg)",
    },
  },
  user: {
    side: "start",
    align: "items-start",
    body: "text-left",
    bubbleClass: "",
    bubble: {
      backgroundColor: "var(--color-bubble-customer)",
      color: "var(--color-text-primary)",
    },
  },
  // Do lado de quem responde, como o agente, mas num balao proprio: o cliente
  // viu o operador no mesmo balao do bot, e aqui o que importa e o contrario,
  // nao creditar ao agente o que um humano escreveu (spec da fase 5, seccao 7).
  // O ambar e o de "Transferida" na lista e no cabecalho, ver o
  // --color-bubble-operator no index.css.
  operator: {
    side: "end",
    align: "items-end",
    body: "text-left",
    bubbleClass: "",
    bubble: {
      backgroundColor: "var(--color-bubble-operator)",
      color: "var(--color-text-primary)",
    },
  },
  quiet: {
    side: null,
    align: "items-center",
    body: "text-center",
    bubbleClass: "",
    bubble: {
      backgroundColor: "var(--color-surface-raised)",
      color: "var(--color-text-secondary)",
    },
  },
};

const DayDivider = ({ label }) => (
  <div className="flex items-center gap-3 px-4 py-2" role="separator" aria-label={label}>
    <span className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} aria-hidden="true" />
    <span className="text-xs font-medium shrink-0" style={{ color: "var(--color-text-secondary)" }}>
      {label}
    </span>
    <span className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} aria-hidden="true" />
  </div>
);

// A pagina em que o cliente estava quando escreveu, so quando muda. Desenha-se
// como o separador de dia, mas sem o `role="separator"`: um separador nao pode
// ter uma ligacao dentro (o WebKit tira-a da arvore de acessibilidade), por isso
// a frase e um paragrafo e so os tracos se escondem. A ligacao vem do
// parsePageUrl, que so devolve http e https, e o rotulo e texto, nunca markup.
// Corta-se como no painel do contacto, com o URL inteiro no `title`: o caminho
// vem do browser do visitante e pode ser comprido.
export const PageDivider = ({ page, translate }) => {
  // Um marcador no lugar do caminho, para partir o texto da traducao sem
  // depender de o caminho nao aparecer no resto da frase.
  const [before, after = ""] = translate("conversationsPageMarker", { path: "\u0001" }).split("\u0001");
  return (
    <div className="flex items-center gap-3 px-4 py-2">
      <span className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} aria-hidden="true" />
      <p className="text-xs font-medium min-w-0 break-all text-center" style={{ color: "var(--color-text-secondary)" }}>
        {before}
        <a href={page.href} title={page.href} target="_blank" rel="noopener noreferrer" className="underline">
          {clipText(page.label).shown}
        </a>
        {after}
      </p>
      <span className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} aria-hidden="true" />
    </div>
  );
};

// O momento em que a conversa passou a um operador, com o nome e a hora.
// Desenha-se como o separador de dia, porque tambem e uma marca no tempo e nao
// uma fala, mas no ambar de "Transferida", a cor que a lista e o cabecalho ja
// dao a fase humana. Sem nome (um `assignee` que o backend leu como ausente)
// diz so que passou a um operador. Ao contrario da data, o rotulo quebra linha:
// o nome de uma conta de equipa no Chatwoot pode nao caber numa, e sem quebrar
// empurrava a transcricao inteira para o lado.
const HandoffDivider = ({ message, translate, language }) => {
  const time = formatClock(message.created_at, language);
  const name = message.assignee?.name;
  const label = name
    ? translate("conversationsHandedOffTo", { name, time })
    : translate("conversationsHandedOffToSomeone", { time });
  return (
    <div className="flex items-center gap-3 px-4 py-2" role="separator" aria-label={label}>
      <span className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} aria-hidden="true" />
      <span
        className="text-xs font-medium min-w-0 text-center"
        style={{ color: "var(--color-status-handed)" }}
        title={formatAbsolute(message.created_at, language)}
      >
        {label}
      </span>
      <span className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} aria-hidden="true" />
    </div>
  );
};

export const Turn = ({
  turn,
  translate,
  language,
  cardLocale,
  priceInfoNotice,
  viewCta,
  contact,
  agent,
  conversationId,
  resolvedAt,
  csat,
}) => {
  const cards = productCardItemsOfTurn(turn);
  const otherCards = otherCardBlocksOfTurn(turn);
  const annotations = annotationNotesOfTurn(turn);
  // O cartao so existe com avaliacao (Tiago, 2026-10-15): sem ela o pedido nao desenha nada.
  const showCsat = Boolean(csat) && turn.items.some((m) => blocksOfType(m, "csat_request").length > 0);
  const label = ROLE_KEYS[turn.role] ? translate(ROLE_KEYS[turn.role]) : turn.role;
  const shape = TURN_SHAPES[turn.role] || TURN_SHAPES.quiet;
  const isAgent = turn.role === "assistant";
  const isOperator = turn.role === "operator";
  const onRight = shape.side === "end";
  // O turno e de um operador so (ver speakerKey), portanto o `sender` da
  // primeira mensagem e o de todas.
  const sender = isOperator ? turn.items[0].sender : null;

  // Lido a cada render, e e isso que faz um `pending` passar a marca sem frame
  // nenhuma: enquanto ha um, o detalhe sonda (ver o `awaitingBridge`) e cada
  // leitura volta a desenhar o turno.
  const now = Date.now();
  // Um turno so com o pedido de avaliacao nao tem nada para a coluna do agente:
  // sem baloes, anexos, marcas nem cartoes, a byline ficaria sozinha, como se o
  // agente tivesse falado a seguir ao operador. Fica so a anotacao, ao centro.
  const hasColumn =
    cards.length > 0 ||
    otherCards.length > 0 ||
    turn.items.some(
      (m) =>
        m.content ||
        blocksOfType(m, "attachment").length > 0 ||
        unknownBlocks(m).length > 0 ||
        bridgeMark(m, now, resolvedAt)
    );

  // Quem falou fecha o turno, por baixo dos baloes, como no widget: um disco de
  // 20px, o nome e a hora numa so linha. O agente e a cara e o nome VERDADEIROS
  // do widget (copy.botAvatarUrl, copy.botName) quando a config os traz; o
  // operador e o nome e a foto que a ponte guardou do Chatwoot, ou a palavra
  // "Operador" numa automacao sem remetente; o cliente e o nome do contacto ou a
  // palavra "Cliente". A linha esta ancorada ao lado de quem fala, com o disco
  // na ponta de fora.
  const speaker = isAgent ? agent.name || label : isOperator ? sender?.name || label : contact.name || label;
  const avatar = !shape.side ? null : isAgent ? (
    <AgentAvatar avatarUrl={agent.avatarUrl} size={20} />
  ) : isOperator ? (
    <OperatorAvatar avatarUrl={sender?.avatar_url} name={sender?.name} size={20} />
  ) : (
    <ContactAvatar seed={contact.seed} name={contact.name} size={20} />
  );

  // Dois limites, e o menor ganha. A percentagem serve os paineis estreitos:
  // um balao a toda a largura nao diz de quem e, e sem ela os dois lados
  // deixavam de se ler como lados. O teto absoluto serve os ecras grandes, e e
  // o mesmo 640px do widget (ai-agent-widget/app/src/ui/bubble.css), onde esta
  // escrito porque: acima disso a linha passa dos 85 caracteres e a leitura
  // confortavel fica entre 50 e 75.
  //
  // A tira de cartoes vive DENTRO desta coluna, portanto herda o mesmo teto a
  // qualquer largura e acaba na mesma margem que os baloes. O min-w-0 e o que
  // permite ao carrossel cortar em vez de empurrar a coluna para alem do teto.
  const runWidth = shape.side ? "max-w-[min(80%,40rem)]" : "max-w-[40rem]";

  return (
    <>
    {/* 8px em cima e em baixo dao os 16px entre turnos do widget; dentro do
        turno o gap e de 2px, que e o que cola as bolhas de uma so resposta. */}
    {hasColumn && (
    <div className={`flex px-4 py-2 ${shape.side ? (onRight ? "justify-end" : "justify-start") : "justify-center"}`}>
      <div className={`flex flex-col gap-0.5 min-w-0 ${runWidth} ${shape.align}`}>
        {turn.items.map((m, i) => {
          const attachments = blocksOfType(m, "attachment");
          const unknown = unknownBlocks(m);
          const mark = bridgeMark(m, now, resolvedAt);
          return (
            <React.Fragment key={m.id}>
              {m.content && (
                <div
                  className={`transcript-bubble min-w-0 px-[14px] py-[10px] ${shape.body} ${shape.bubbleClass}`}
                  style={{ ...shape.bubble, ...bubbleRadius(shape.side, i > 0) }}
                >
                  {/* HTML produzido pelo markdown-it e saneado pelo DOMPurify
                      em src/utils/markdown.js, a mesma justificacao com que o
                      widget faz esta chamada. Sem isto o operador lia
                      `**329,99€**` e bullets de asterisco, e julgava o agente
                      por texto que ele nao escreveu. Dentro do balao a prosa
                      herda a cor do balao, ver .transcript-bubble no
                      src/index.css. */}
                  <div
                    className="transcript-prose"
                    dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(m.content) }}
                  />
                </div>
              )}
              {attachments.length > 0 && (
                // Os chips ja trazem moldura e fundo proprios. Enfia-los num
                // balao dava duas molduras, portanto ficam soltos e so alinham
                // com o lado do turno.
                <div className={`flex flex-wrap gap-2 ${onRight ? "justify-end" : ""}`}>
                  {attachments.map((b, j) => (
                    // A chave e o attachment_id; cai para o indice so se um
                    // bloco malformado nao o tiver, para nao rebentar o render.
                    <AttachmentBlock
                      key={b.payload?.attachment_id || j}
                      payload={b.payload || {}}
                      conversationId={conversationId}
                      translate={translate}
                    />
                  ))}
                </div>
              )}
              {unknown.map((b, j) => (
                <p key={j} className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  {b.type}
                </p>
              ))}
              {/* Por baixo da mensagem a que diz respeito, e nao na byline: um
                  turno junta varias mensagens e so uma pode ter ficado pelo
                  caminho. */}
              {mark && <BridgeMarkLine mark={mark} translate={translate} />}
            </React.Fragment>
          );
        })}

        {/* Uma tira de opcoes ou um card curado, em texto: a nota que o writer
            escreveu para esta superficie e, por baixo, os titulos que o cliente
            viu. Nao e o desenho do widget e nao tenta ser; o que faltava ao
            operador era saber o que foi enviado. */}
        {otherCards.map((block, i) => (
          <div key={i} className="w-full min-w-0 mt-1">
            {block.payload.operator_note && (
              <p className="text-xs italic" style={{ color: "var(--color-text-secondary)" }}>
                {stripEmphasis(block.payload.operator_note)}
              </p>
            )}
            {hasTitles(block) && (
              <ul
                className="list-disc pl-4 text-[13px] leading-[18px]"
                style={{ color: "var(--color-text-secondary)" }}
              >
                {block.payload.items
                  .filter((item) => item.title)
                  .map((item, j) => (
                    <li key={j}>{item.title}</li>
                  ))}
              </ul>
            )}
          </div>
        ))}

        {/* Os cartoes ficam fora do balao (sao uma tira que rola) mas dentro da
            coluna do turno, para acabarem na mesma margem que os baloes. O
            w-full e o que da a tira uma largura definida para cortar. */}
        {cards.length > 0 && (
          <div className="w-full min-w-0 mt-1">
            <ProductCardStrip
              items={cards}
              locale={cardLocale}
              priceInfoNotice={priceInfoNotice}
              viewCta={viewCta}
            />
          </div>
        )}

        {/* A byline: uma vez por turno, por baixo da corrida, como no widget e
            no Chatwoot. E essa a razao de as mensagens consecutivas serem
            dobradas la em cima. O disco e decorativo aqui, o nome esta ao lado
            em texto. */}
        <div
          className={`flex items-center gap-1.5 mt-1 max-w-full ${onRight ? "flex-row-reverse" : ""}`}
          style={{ color: "var(--color-text-secondary)" }}
        >
          {avatar}
          <span className="text-xs font-medium leading-4 truncate">{speaker}</span>
          <time
            className="text-[11px] leading-4 tabular-nums shrink-0"
            dateTime={turn.at}
            title={formatAbsolute(turn.at, language)}
          >
            {formatClock(turn.at, language)}
          </time>
        </div>
      </div>
    </div>
    )}

    {/* O que aconteceu a conversa, e nao o que o agente disse: a nota da oferta do
        botao de terminar conversa e, depois, a avaliacao do cliente (`csat` vem da
        conversa, e `undefined` de um backend antigo passa tal e qual). Ao centro da
        transcricao, como os separadores de dia e o do handoff, e fora da coluna do
        agente, que encosta a direita (visto no browser: ambos a ras da margem
        esquerda dessa coluna). Por baixo da byline, em ordem cronologica: a oferta
        veio antes do pedido, e este antes da resposta. */}
    {(annotations.length > 0 || showCsat) && (
      <div data-testid="turn-annotations" className="flex flex-col items-center gap-6 px-4 pb-2">
        {annotations.map((note, i) => (
          <p key={i} className="text-xs italic text-center" style={{ color: "var(--color-text-secondary)" }}>
            {stripEmphasis(note)}
          </p>
        ))}
        {showCsat && (
          <div className="w-full max-w-[28rem] min-w-0">
            <ConversationCsat csat={csat} translate={translate} language={language} />
          </div>
        )}
      </div>
    )}
    </>
  );
};

// Alturas em pixeis porque sao contas, nao gostos: o balao tem 10px de padding
// em cima e em baixo e a linha da prosa mede 0.875rem x 1.625, ou seja 22.75px.
// Uma linha da 43px, duas dao 66px, tres dao 88px. Larguras em percentagem para
// acompanharem o painel, sempre abaixo do tecto de 80% dos turnos a serio.
//
// O balao do agente NAO leva a primaria do tenant. Um rectangulo azul do lado
// direito le-se como uma mensagem que ja chegou, e nao chegou nada: o esqueleto
// nao e conteudo e nao se pinta como tal.
const SKELETON_TURNS = [
  { agent: false, width: "w-[45%]", height: "h-[43px]" },
  { agent: true, width: "w-[72%]", height: "h-[88px]" },
  { agent: false, width: "w-[38%]", height: "h-[43px]" },
  { agent: true, width: "w-[64%]", height: "h-[66px]" },
  { agent: false, width: "w-[55%]", height: "h-[43px]" },
];

// Mesma geometria do `Turn` e do cabecalho verdadeiros, incluindo a byline e o
// canto de 4px do lado de quem fala: o cabecalho de 56px e o separador de dia
// ocupam ja a altura que a transcricao carregada vai ocupar, portanto o
// primeiro turno a chegar aterra onde estava o primeiro esqueleto.
const TranscriptSkeleton = ({ label, back }) => (
  <div
    role="status"
    aria-busy="true"
    aria-label={label}
    className="flex-1 min-w-0 min-h-0 flex flex-col"
  >
    <header className={`${COLUMN_HEADER_CLASS} gap-3`}>
      {back}
      <Skeleton variant="custom" width="w-8" height="h-8" rounded="rounded-full" />
      <div className="min-w-0 flex-1">
        <div className="h-5 flex items-center">
          <Skeleton variant="custom" width="w-40" height="h-3.5" rounded="rounded" />
        </div>
        <div className="h-4 flex items-center">
          <Skeleton variant="custom" width="w-56" height="h-3" rounded="rounded" />
        </div>
      </div>
      <Skeleton variant="custom" width="w-16" height="h-[22px]" rounded="rounded-full" />
    </header>

    <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable]">
      <div className="flex flex-col py-2">
        {/* A caixa de 16px e a linha de texto do rotulo verdadeiro (text-xs):
            sem ela o separador media 28px em vez de 32 e o primeiro turno
            descia 4px quando a transcricao chegava. Medido. */}
        <div className="flex items-center gap-3 px-4 py-2">
          <span className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} />
          <span className="h-4 flex items-center">
            <Skeleton variant="custom" width="w-12" height="h-3" rounded="rounded" />
          </span>
          <span className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} />
        </div>
        {SKELETON_TURNS.map((t, i) => (
          <div key={i} className={`flex px-4 py-2 ${t.agent ? "justify-end" : "justify-start"}`}>
            <div className={`flex flex-col min-w-0 ${t.width} max-w-[min(80%,40rem)] ${t.agent ? "items-end" : "items-start"}`}>
              <Skeleton
                variant="custom"
                width="w-full"
                height={t.height}
                rounded="rounded-[20px]"
                className={t.agent ? "rounded-br-[4px]" : "rounded-bl-[4px]"}
              />
              {/* A byline: um disco de 20px e uma barra, na caixa de 20px que a
                  linha verdadeira ocupa. */}
              <div className={`flex items-center gap-1.5 h-5 mt-1 ${t.agent ? "flex-row-reverse" : ""}`}>
                <Skeleton variant="custom" width="w-5" height="h-5" rounded="rounded-full" />
                <Skeleton variant="custom" width="w-20" height="h-3" rounded="rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// `onLoaded` deixa a coluna do contacto reusar esta leitura em vez de repetir
// o pedido do detalhe, que traz a transcricao inteira so para chegar ao
// contacto. Sobe em cada leitura bem sucedida, silenciosa incluida, para o
// painel acompanhar um contacto que mude a meio.
//
// `contactPanelOpen` e `onToggleContactPanel` vem da concha, que e quem
// renderiza a coluna do contacto; o botao vive aqui porque o cabecalho da
// transcricao e o sitio onde o operador espera encontra-lo.
const ConversationDetail = ({ conversationId, onLoaded, contactPanelOpen, onToggleContactPanel }) => {
  const { translate, language } = useLanguage();
  const { chats } = useChat();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const widgetConfig = useChatWidgetConfig(conversation?.chat_id);
  const widgetTheme = widgetConfig?.theme;
  const widgetLocale = widgetConfig?.locale;
  // Chat's legal price disclaimer, same config the theme and locale above
  // already come from. Passed straight down to ProductCardStrip, which never
  // fetches it itself, see that file's header comment.
  const priceInfoNotice = widgetConfig?.copy?.priceInfoNotice;
  // A mesma bandeira com que o widget decide se o cartao acaba num botao. Vem
  // da config do chat, e nao do payload, porque todos os cartoes tem um
  // `cta_url` e nada no payload diria se ESTE tenant acaba o tile com botao.
  const viewCta = widgetConfig?.features?.productCardViewCta ?? false;
  // A cara e o nome do agente, tal como o widget os mostra ao cliente: sao a
  // mesma `copy` de onde vem o aviso de preco.
  const agent = {
    avatarUrl: widgetConfig?.copy?.botAvatarUrl,
    name: widgetConfig?.copy?.botName,
  };
  // O CSS que o gestor do tenant escreveu para o widget, delimitado a
  // transcricao. Chega pela mesma config que o tema, o locale e o aviso de
  // preco. Fica aqui em cima, com os outros hooks, e nao la em baixo ao pe do
  // `transcriptStyle`, porque abaixo ha `return`s de carregamento e de erro e
  // um hook depois deles quebrava a ordem dos hooks entre renders.
  const scopedPanelCss = useMemo(
    () => scopePanelCustomCss(widgetTheme?.panelCustomCSS, `.${TRANSCRIPT_CLASS}`),
    [widgetTheme?.panelCustomCSS]
  );
  // O fundo que o tenant deu ao balao de entrada do widget, ou seja ao balao
  // em que o cliente le o agente. Sai do mesmo `panelCustomCSS` que ja esta
  // ali em cima, pelo parser do browser e nao por nos.
  const widgetBubbleBg = useMemo(
    () =>
      readScopedCustomProperty(
        widgetTheme?.panelCustomCSS,
        `.${TRANSCRIPT_CLASS}`,
        "--kodly-ai-agent-bubble-incoming-bg"
      ),
    [widgetTheme?.panelCustomCSS]
  );

  // Contador de geracao a maneira do ChatContext (loadGeneration.current), e nao
  // o `let current = true` do useChatWidgetConfig: aqui o `load` e partilhado
  // pelo efeito de montagem e pelo do polling, portanto a marca nao pode viver
  // dentro do corpo de um efeito. Guarda duas coisas: um tick lento a aterrar em
  // cima de um mais recente, e sobretudo o `onLoaded`, que escreve no painel do
  // contacto la em cima e chegaria la depois de esta instancia ja nao existir.
  const loadGeneration = useRef(0);

  // Se alguma leitura ja trouxe dados. Nao e o mesmo que `conversation !== null`
  // visto de dentro do `load`: o `load` e um useCallback que so depende de
  // [conversationId, onLoaded], portanto o `conversation` que ele fecha por cima
  // e o do render que o criou e fica velho.
  const hasDataRef = useRef(false);

  // Devolve se a leitura correu bem, e nao void, porque o useConversationLive
  // precisa de saber: um tick silencioso que falhe engole o erro de proposito
  // (para nao interromper quem esta a ler), e com a sondagem desligada nada mais
  // voltaria a tentar. O booleano e o que lhe permite reabrir o fio.
  const load = useCallback(
    async ({ silent = false } = {}) => {
      loadGeneration.current += 1;
      const myGeneration = loadGeneration.current;
      const current = () => myGeneration === loadGeneration.current;
      if (!silent) setLoading(true);
      // O "a carregar" apaga-se em quem tem algo para mostrar, e nao no finally. No
      // finally exigia `!silent` e `current()` ao mesmo tempo, e nesta fase o caminho
      // normal falha as duas: a leitura da montagem e ultrapassada pelo pedido que o
      // `live` provoca, portanto nao e current, e quem a ultrapassa e silencioso. O
      // esqueleto ficava para sempre.
      //
      // Uma leitura silenciosa que falhe sem haver dados nao apaga nada, e isso esta
      // certo: nao ha nada para mostrar. Nao fica preso, porque o hook chama
      // stream.reconnect() num pedido falhado, o que, com o fio a servir, produz um
      // `live` novo e outra tentativa, e sem ele devolve a sondagem ao servico.
      try {
        const data = await conversationsApi.get(conversationId);
        // Quem foi ultrapassado nao falhou, foi substituido por uma leitura
        // mais recente, e e essa que responde pelo resultado.
        if (!current()) return true;
        setConversation(data);
        hasDataRef.current = true;
        if (onLoaded) onLoaded(data);
        setNotFound(false);
        setFailed(false);
        setLoading(false);
        return true;
      } catch (error) {
        console.error("conversation detail failed", error);
        // Mesma razao do sucesso: quem foi ultrapassado nao falhou.
        if (!current()) return true;
        // Uma leitura silenciosa nao substitui uma transcricao que o operador
        // esta a ler, e essa e a razao de todo o `!silent` aqui. Mas quando
        // ainda nao ha nada no ecra nao ha nada para proteger, e ficar calado
        // deixa dois esqueletos a girar sem mensagem e sem "tentar novamente".
        // Acontece no caminho normal desta fase: a leitura da montagem e
        // ultrapassada pela que o `live` provoca, e essa e silenciosa.
        const surface = !silent || !hasDataRef.current;
        // O 422 e o mesmo "nao existe" que o 404, so que dito mais cedo: o
        // endpoint tipa o id do caminho como UUID, portanto /conversations/abc
        // nem chega a ser procurado. Sem isto caia no ramo generico, que
        // oferece um "tentar novamente" que ia falhar sempre da mesma maneira.
        const status = error.response?.status;
        if (status === 404 || status === 422) {
          // Um tick silencioso nao pode substituir uma transcricao que o
          // operador esta a ler por "conversa nao encontrada".
          if (surface) {
            setNotFound(true);
            setLoading(false);
          }
        } else if (surface) {
          // Um 500, um 403 ou a rede em baixo. Sem este ramo o render adiante
          // desreferencia `conversation` a null e a pagina fica branca.
          setFailed(true);
          setLoading(false);
        }
        // A coluna do contacto espera por esta leitura para saber o contacto.
        // Sem lhe dizer que falhou, o esqueleto dela ficava a girar para
        // sempre ao lado de uma transcricao que ja desistiu.
        if (surface && onLoaded) onLoaded(null);
        return false;
      }
    },
    [conversationId, onLoaded]
  );

  useEffect(() => {
    load();
    // Ao desmontar, o que estiver no ar fica invalidado. A `key` la em cima ja
    // troca a instancia quando a conversa muda, e sem isto a resposta da
    // conversa anterior ainda chamaria o `onLoaded` do painel da seguinte.
    return () => {
      loadGeneration.current += 1;
    };
  }, [load]);

  // O `onChange` do hook e o load silencioso: um evento do fio vale exactamente
  // o que vale um tick, e nao mais. Silencioso porque uma mensagem nova nao pode
  // pintar o esqueleto de carregamento por cima do que o operador esta a ler.
  const refetch = useCallback(() => load({ silent: true }), [load]);
  // O `status` da conversa entra no hook, e nao so o id: e ele que fecha o fio
  // quando a leitura revela uma conversa que ja nao esta aberta, que e o caso
  // que o `resolved` perdido deixaria passar.
  const { status: liveStatus, typing } = useConversationLive(conversationId, {
    onChange: refetch,
    conversationStatus: conversation?.status,
  });

  // Uma mensagem do cliente ainda `pending` muda de estado sem frame nenhuma: o
  // despachante da ponte grava o `forwarded` ou o `rejected` e nao publica nada
  // no fio. Com o fio vivo e sem sondagem, a leitura ficava com o `pending`, e
  // passado um minuto a marca de "ainda nao chegou" aparecia numa mensagem ja
  // entregue. Decidido pelo Tiago a 2026-10-01: o detalhe continua a ser a unica
  // fonte, e e ele que se volta a ler enquanto houver uma.
  const awaitingBridge = Boolean(conversation?.messages?.some((m) => m.bridge_status === "pending"));

  useEffect(() => {
    // Nunca os dois ao mesmo tempo, menos enquanto uma mensagem espera pela
    // ponte (ver o `awaitingBridge`). Com o fio a servir, um tick de 10 segundos
    // e um segundo escritor do mesmo estado e um pedido que nao traz nada.
    //
    // O `ended` CONTINUA a sondar, e isso e deliberado. Uma conversa resolvida
    // ainda pode crescer: nada predica uma escrita de mensagem no status da
    // conversa, o append_text e o set_status olham so para messages.status,
    // portanto um turno em voo quando o visitante clica em terminar acaba de
    // escrever na conversa ja resolvida. Com o fio fechado pelo `resolved`, a
    // sondagem e a unica coisa que apanha essa cauda. E o custo e o de hoje: a
    // 6.1 sonda todas as conversas, resolvidas incluidas.
    if (liveStatus === "live" && !awaitingBridge) return undefined;
    const timer = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [load, liveStatus, awaitingBridge]);

  // Acima de lg a lista esta ao lado e nao ha para onde voltar.
  const back = (
    <button
      type="button"
      onClick={() => navigate("/conversations")}
      aria-label={translate("conversationsBack")}
      title={translate("conversationsBack")}
      className="lg:hidden flex items-center justify-center w-8 h-8 -ml-2 rounded-lg shrink-0 hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
      style={{ color: "var(--color-text-secondary)" }}
    >
      <ArrowLeft size={16} aria-hidden="true" />
    </button>
  );

  if (loading) {
    return <TranscriptSkeleton label={translate("loading")} back={back} />;
  }

  if (notFound || failed || !conversation) {
    return (
      <div className="flex-1 min-w-0 flex flex-col">
        <header className={`${COLUMN_HEADER_CLASS} gap-3`}>{back}</header>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <span
            className="flex items-center justify-center w-10 h-10 rounded-full"
            style={{ backgroundColor: "var(--color-surface-raised)" }}
          >
            {notFound ? (
              <Inbox size={20} style={{ color: "var(--color-text-secondary)" }} aria-hidden="true" />
            ) : (
              <AlertCircle size={20} style={{ color: "var(--color-text-secondary)" }} aria-hidden="true" />
            )}
          </span>
          <p className="text-sm font-medium mt-3" style={{ color: "var(--color-text-primary)" }}>
            {translate(notFound ? "conversationsNotFound" : "conversationsDetailFailed")}
          </p>
          {!notFound && (
            <button
              type="button"
              onClick={() => load()}
              className="mt-3 text-[13px] font-medium underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
              style={{ color: "var(--color-text-primary)" }}
            >
              {translate("conversationsRetry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  const turns = groupIntoTurns(conversation.messages);
  const pageMarks = pageMarkers(turns, conversation.store_origins);
  const chatName = chats.find((c) => c.id === conversation.chat_id)?.name || "";
  const manyChats = chats.length > 1;
  const statusLabel = STATUS_KEYS[conversation.status]
    ? translate(STATUS_KEYS[conversation.status])
    : conversation.status;
  const anonymous = !conversation.contact_label;
  // A semente do disco e a mesma que a lista usa para esta conversa, para o
  // operador ver o mesmo disco na linha em que clicou e no cabecalho.
  const contact = {
    seed: conversation.contact_label || conversation.contact?.id,
    name: conversation.contact_label,
  };

  // As cores do tenant entram como custom properties no proprio contentor da
  // transcricao, nunca em :root: o resto do backoffice nao e do tenant e nao
  // pode ganhar o azul da Radio Popular por causa desta pagina. Sem cor valida
  // nao se declara nada e o `.transcript` do src/index.css fica com os
  // defaults, que sao os do widget sem tenant.
  //
  // Sao dois pares de nomes com dois donos diferentes:
  //  - `--transcript-agent-*` sao nossos e pintam os baloes. O valor pode ser
  //    do tenant na mesma, ver a escolha do balao do agente mais abaixo;
  //  - `--kodly-ai-agent-primary` / `-secondary` sao os do WIDGET e sao os que
  //    o cartao le, com os mesmos nomes que o cliente viu. Escritos em linha,
  //    tal como o widget os escreve (`root.style.setProperty` no
  //    theme-provider), portanto o panelCustomCSS nao lhes ganha, o que esta
  //    certo: saem os dois da mesma `theme` do mesmo chat.
  // O `-primary-contrast` fica de fora de proposito, porque o widget tambem
  // nao o deriva do tema; e branco fixo no default-tokens.css.
  const primary = widgetTheme?.primary;
  const secondary = widgetTheme?.secondary;
  // `undefined` do hook e "ainda nao sabemos", `null` e "sabemos que nao ha".
  // Enquanto nao sabemos, o balao do agente fica na superficie neutra em vez do
  // par de recurso da casa: medido, o tema chegava depois dos baloes e eles
  // mudavam de verde para o azul do tenant a meio da leitura. Cinzento para
  // azul e a mesma transicao sem a cor errada pelo meio, e nao atrasa nada,
  // que era o custo de segurar a transcricao a espera de decoracao.
  const themeUnknown = widgetConfig === undefined;

  // O fundo do balao do agente, por esta ordem:
  //  1. o balao de entrada que o tenant pintou no widget, que e o unico sitio
  //     onde o cliente leu mesmo aquelas respostas. Num tenant que o tenha
  //     pintado, a primaria e a cor do balao DO CLIENTE e nunca a do agente,
  //     portanto usa-la aqui mostrava a resposta numa cor que o cliente nunca
  //     viu por baixo dela (o MODZ pinta o balao de #feeff1 e a primaria dele
  //     e o #F15365 do balao de quem escreve).
  //  2. a primaria do chat, para quem nao pintou nada. Fica o que o backoffice
  //     sempre mostrou, e nao dois cinzentos iguais: o default do widget para
  //     este balao e #f4f5f7 e o balao do cliente aqui e #f1f3f5, que dao
  //     1.02:1 um contra o outro e deixavam a transcricao sem lados.
  //  3. nada, e o `.transcript` do src/index.css fica com o par da casa.
  //
  // O primeiro plano sai sempre do fundo que se escolheu, pela aritmetica da
  // WCAG, e nunca do `--kodly-ai-agent-text` do tenant: o par tem de vir
  // inteiro do mesmo sitio. Meio de cada lado era o caso da Delta, que pinta o
  // balao de #f6f5ee e nao mexe no texto, e que assim ficaria com o branco
  // calculado para o #e4022b da primaria por cima de um creme.
  // Nos tons claros que os tenants usam a formula devolve o que o widget ja
  // mostra (o MODZ declara #020708, a formula da #000000), e a um tenant que
  // pinte o balao escuro sem mexer no texto o operador continua a ler.
  // NAO se acrescenta aqui uma aresta ao balao que vem do widget, e ja se
  // tentou. O argumento parece bom: o balao palido e o `--color-bubble-customer`
  // (#f1f3f5) do lado de la tem a MESMA luminancia, 1.00:1 de contraste WCAG.
  // So que a razao de contraste mede luminancia, que e a pergunta certa para
  // texto sobre fundo e a errada para duas superficies lado a lado. Pela
  // diferenca perceptual, que e a pergunta certa, o MODZ esta a dE2000 7.71 do
  // balao do cliente e a 7.32 da folha, e a Delta a 4.52 e 4.01, quando o
  // limiar do que se nota anda em 2.3. O unico par indistinguivel e o default
  // do widget (#f4f5f7, dE 0.67), e esse nunca chega aqui: quem nao declara o
  // token fica com a primaria cheia, na linha abaixo. E uma aresta so de um dos
  // lados le-se como falta de acabamento, que foi o que a fez cair.
  const widgetBubble = isHexColor(widgetBubbleBg) ? widgetBubbleBg.trim() : null;
  const agentBubbleBg =
    widgetBubble ?? (isHexColor(primary) ? primary.trim() : null);

  const transcriptStyle = {
    ...(themeUnknown
      ? {
          "--transcript-agent-bg": "var(--color-surface-raised)",
          "--transcript-agent-fg": "var(--color-text-primary)",
        }
      : {}),
    ...(agentBubbleBg
      ? {
          "--transcript-agent-bg": agentBubbleBg,
          "--transcript-agent-fg": readableForeground(agentBubbleBg),
        }
      : {}),
    // O cartao le a primaria com o nome do widget, pinte-se o balao com ela ou
    // nao: sao duas perguntas diferentes a mesma `theme`.
    ...(isHexColor(primary)
      ? { "--kodly-ai-agent-primary": primary.trim() }
      : {}),
    // Independente da primaria: o widget escreve cada token por si, e um chat
    // com secundaria valida e primaria invalida ainda tem o preco certo.
    ...(isHexColor(secondary)
      ? { "--kodly-ai-agent-secondary": secondary.trim() }
      : {}),
  };

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col">
      <header className={`${COLUMN_HEADER_CLASS} gap-3`}>
        {back}
        <ContactAvatar seed={contact.seed} name={contact.name} size={32} />
        <div className="min-w-0 flex-1">
          <h2
            className={`text-[15px] leading-5 truncate ${anonymous ? "font-medium" : "font-semibold"}`}
            style={{ color: anonymous ? "var(--color-text-secondary)" : "var(--color-text-primary)" }}
          >
            {conversation.contact_label || translate("conversationsAnonymous")}
          </h2>
          {/* Dois factos separados por espaco, sem pontos no meio: quando ha
              um so chat o nome dele nao diz nada e nao aparece. */}
          <p className="flex items-center gap-3 text-xs leading-4 truncate" style={{ color: "var(--color-text-secondary)" }}>
            <ConversationBotTag bot={conversation.bot} inSubtitle />
            <span className="truncate" title={formatAbsolute(conversation.created_at, language)}>
              {translate("conversationsStartedAgo", {
                when: formatRelative(conversation.created_at, language),
              })}
            </span>
            {manyChats && chatName && <span className="truncate">{chatName}</span>}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <ConversationStatusPill status={conversation.status} label={statusLabel} />
          {/* Duas pastilhas porque sao dois factos: o `status` diz aberta ou
              resolvida e o `handed_off` diz se ja passou por um humano. Nada
              escreve `status = 'handed_off'`, portanto nao ha aqui repeticao
              possivel de evitar. */}
          {conversation.handed_off && (
            <ConversationStatusPill status="handed_off" label={translate("conversationsStatusHandedOff")} />
          )}
          {/* A coluna do contacto e do operador: abre-se e fecha-se aqui, e a
              escolha fica guardada. Abaixo de lg nao ha terceira coluna para
              mostrar, portanto nao ha botao. */}
          <button
            type="button"
            onClick={onToggleContactPanel}
            // aria-expanded e nao aria-pressed: isto mostra e esconde uma
            // regiao. Com os dois, um leitor de ecra dizia "ocultar contacto,
            // premido", que se le como escondido estando a coluna a vista. O
            // nome fica fixo e e o estado que muda, que e a forma da
            // divulgacao. Sem aria-controls: a coluna nem existe no DOM quando
            // esta fechada, e apontar para um id ausente e pior do que nao
            // apontar.
            aria-expanded={contactPanelOpen}
            aria-label={translate("conversationsContactPanel")}
            title={translate(contactPanelOpen ? "conversationsHideContact" : "conversationsShowContact")}
            className="hidden lg:flex items-center justify-center w-8 h-8 ml-1 rounded-lg transition-colors duration-[120ms] hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
            style={{ color: contactPanelOpen ? "var(--color-text-primary)" : "var(--color-text-secondary)" }}
          >
            {contactPanelOpen ? (
              <PanelRightClose size={16} aria-hidden="true" />
            ) : (
              <PanelRightOpen size={16} aria-hidden="true" />
            )}
          </button>
        </div>
      </header>

      <div
        // O gutter reservado tambem aqui: e a mesma coluna do esqueleto e a
        // largura util tem de ser a mesma nos dois, senao os baloes estreitam
        // 6px no instante em que a transcricao chega.
        className="flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable]"
        // A transcricao rola e cresce com o poll. Sem nome e sem tabIndex nao ha
        // como a percorrer pelo teclado nem como saber, num leitor de ecra, que
        // regiao e esta.
        tabIndex={0}
        role="log"
        aria-label={translate("conversationsTranscriptLabel")}
      >
        <div className={`${TRANSCRIPT_CLASS} flex flex-col py-2`} style={transcriptStyle}>
          {/* O CSS do tenant, delimitado a esta arvore. Fica DEPOIS do
              index.css na ordem do documento (isto e <body>, o index.css e
              <head>), que e o que faz o `:scope` dele ganhar aos defaults com a
              mesma especificidade. Ver utils/panelCustomCss.js para o que a
              delimitacao contem e, sobretudo, para o que nao contem. */}
          {scopedPanelCss && <style>{scopedPanelCss}</style>}
          {turns.map((turn, i) => {
            // Um separador antes do primeiro turno, sempre, e antes de cada
            // turno cujo dia civil e diferente do anterior: e a unica data
            // visivel na transcricao, o cabecalho so diz ha quanto tempo. A
            // data vai por extenso e nunca "hoje" ou "ontem": uma transcricao
            // le-se dias depois, e "ontem" muda de sentido a cada meia-noite.
            const day = dayKey(turn.at);
            const newDay = i === 0 || day !== dayKey(turns[i - 1].at);
            return (
              <React.Fragment key={turn.items[0]?.id || i}>
                {newDay && <DayDivider label={formatDay(turn.at, language)} />}
                {pageMarks[i] && <PageDivider page={pageMarks[i]} translate={translate} />}
                {isHandoffDivider(turn.items[0]) ? (
                  <HandoffDivider message={turn.items[0]} translate={translate} language={language} />
                ) : (
                  <Turn
                    turn={turn}
                    translate={translate}
                    language={language}
                    cardLocale={widgetLocale}
                    priceInfoNotice={priceInfoNotice}
                    viewCta={viewCta}
                    contact={contact}
                    agent={agent}
                    conversationId={conversation.id}
                    resolvedAt={conversation.resolved_at}
                    csat={conversation.csat}
                  />
                )}
              </React.Fragment>
            );
          })}

          {/* Dentro do contentor com scroll, e nao a seguir a ele: fora dele o
              aviso ficava fixo no fundo do painel em vez de acompanhar o
              ultimo turno, que e o lugar onde um "esta a escrever" se le. */}
          {typing && (
            <p className="px-4 pb-2 text-xs italic text-right" style={{ color: "var(--color-text-secondary)" }}>
              {translate("conversationsAgentTyping")}
            </p>
          )}
        </div>
      </div>

      {/* No sitio onde estaria a caixa de resposta, e fora da zona que rola:
          o backoffice so le, e quem quer responder a uma conversa que passou a
          um operador responde no Chatwoot. O link vem montado do backend, e so
          numa conversa que chegou a fase humana; null numa conta que perdeu o
          URL publico ou o id no Chatwoot, e ai nao ha botao (spec da fase 5,
          seccao 7). */}
      {conversation.chatwoot_url && (
        <footer className="flex items-center justify-end h-14 px-4 shrink-0 border-t border-[var(--color-border)]">
          <a
            href={conversation.chatwoot_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
          >
            <ExternalLink size={14} aria-hidden="true" />
            {translate("conversationsOpenInChatwoot")}
            {/* O separador novo diz-se a quem nao ve o icone. */}
            <span className="sr-only"> {translate("conversationsOpensInNewTab")}</span>
          </a>
        </footer>
      )}
    </div>
  );
};

export default ConversationDetail;
